<template>
  <li
    class="play-breadcrumb-item"
    :class="{ 'is-active': active }"
  >
    <component
      :is="resolvedTag"
      class="play-breadcrumb-item__link"
      :to="resolvedTag === 'router-link' ? to : null"
      :href="resolvedTag === 'a' ? (href || null) : null"
      :aria-current="active ? 'page' : null"
      v-bind="$attrs"
      v-on="$listeners"
    >
      <!-- VUE3-MIGRATION: $listeners removed in Vue 3 (merged into $attrs).
         On migration delete `v-on="$listeners"` — v-bind="$attrs" forwards listeners too. -->
      <slot />
    </component>
  </li>
</template>

<script>
/**
 * PlayBreadcrumbItem — one crumb inside <PlayBreadcrumb>.
 *
 * Token-driven (see src/styles/tokens.scss). Replaces bootstrap-vue's
 * <b-breadcrumb-item>. Renders a <router-link> (`to`) or <a> (`href`) link, or
 * plain text when `active` (the current page) / when neither is set.
 *
 * VUE3-MIGRATION: `v-on="$listeners"` is the only Vue 2-ism; drop it on Vue 3
 * (v-bind="$attrs" keeps forwarding listeners). Props/slots are Vue 3-safe.
 */
export default {
  name: 'PlayBreadcrumbItem',
  inheritAttrs: false,
  props: {
    to: {
      type: [String, Object],
      default: null,
    },
    href: {
      type: String,
      default: '',
    },
    /** Current page — renders as plain text */
    active: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    resolvedTag() {
      if (this.active) return 'span'
      if (this.to) return 'router-link'
      if (this.href) return 'a'
      return 'span'
    },
  },
}
</script>

<style lang="scss" scoped>
.play-breadcrumb-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--text-muted);

  /* separator */
  &:not(:first-child)::before {
    content: '/';
    color: var(--text-muted);
    font-weight: 700;
  }

  &__link {
    display: inline-flex;
    align-items: center;
    color: var(--c-secondary);
    font-weight: 600;
    text-decoration: none;
    transition: color var(--dur-fast) var(--ease-out);

    &:hover { color: var(--c-secondary-strong); }
    &:focus-visible { outline: 2px solid var(--c-secondary); outline-offset: 2px; border-radius: 2px; }
  }

  &.is-active .play-breadcrumb-item__link {
    color: var(--text-strong);
    font-weight: 700;
    pointer-events: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .play-breadcrumb-item__link { transition: none; }
}
</style>
